import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useContent } from '../hooks/useContent';
import { useToast } from '../hooks/useToast';
import { useUnsavedChanges } from '../hooks/useUnsavedChanges';
import { getContentId } from '../types';
import FormField from '../components/FormField';
import HelpSidebar from '../components/HelpSidebar';
import { pageHelp } from '../data/helpContent';
import '../styles/editor.css';

export default function CategoryEditor() {
  const { siteId, id } = useParams<{ siteId: string; id: string }>();
  const navigate = useNavigate();
  const { items, loading, create, update } = useContent(siteId, 'category');
  const { addToast } = useToast();

  const isNew = id === 'new';
  const existingItem = isNew
    ? undefined
    : items.find((item) => getContentId(item.contentKey) === id);

  const [formData, setFormData] = useState<Record<string, unknown>>({});
  const [originalData, setOriginalData] = useState<Record<string, unknown>>({});
  const [saving, setSaving] = useState(false);
  const justCreatedRef = useRef(false);

  const fields = {
    name: { type: 'text' as const, label: 'Category Name', required: true, maxLength: 50, hint: 'The name visitors see when filtering products (e.g., "Shortboards", "Prints").' },
    description: { type: 'text' as const, label: 'Description', maxLength: 200, hint: 'A short description of this category. Optional.' },
    sortOrder: { type: 'text' as const, label: 'Sort Order', placeholder: '0', hint: 'Controls the order categories appear. Lower numbers show first (0, 1, 2, etc.).' },
  };

  // For new items: dirty if any field has a non-empty value
  // For existing items: dirty if formData differs from originalData
  const isDirty = justCreatedRef.current
    ? false
    : isNew
      ? Object.values(formData).some((v) => v !== undefined && v !== '' && !(Array.isArray(v) && v.length === 0))
      : JSON.stringify(formData) !== JSON.stringify(originalData);
  useUnsavedChanges(isDirty);

  useEffect(() => {
    if (existingItem) {
      const data = existingItem.data || {};
      setFormData(data);
      setOriginalData(data);
    }
  }, [existingItem]);

  useEffect(() => {
    if (id !== 'new') {
      justCreatedRef.current = false;
    }
  }, [id]);

  const handleChange = (name: string, value: unknown) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const validate = (): boolean => {
    const name = formData.name;
    if (!name || (typeof name === 'string' && !name.trim())) {
      addToast('error', 'Category Name is required');
      return false;
    }
    return true;
  };

  const handleSave = async () => {
    if (!siteId || !validate()) return;
    setSaving(true);
    try {
      if (isNew) {
        justCreatedRef.current = true;
        const created = await create(formData, items.length);
        addToast('success', 'Category created');
        const newId = getContentId(created.contentKey);
        navigate(`/sites/${siteId}/content/categories/${newId}`, { replace: true });
      } else if (id) {
        await update(id, formData);
        setOriginalData({ ...formData });
        addToast('success', 'Category updated');
      }
    } catch (err) {
      justCreatedRef.current = false;
      addToast('error', err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="page">
        <div className="loading-state"><div className="spinner" /></div>
      </div>
    );
  }

  if (!isNew && !existingItem && !loading) {
    return (
      <div className="page">
        <div className="error-state">
          <p>Category not found.</p>
          <button className="btn btn-secondary" onClick={() => navigate(-1)}>
            Go back
          </button>
        </div>
      </div>
    );
  }

  const help = pageHelp.category;

  return (
    <div className="page">
      <div className="page-header">
        <div className="page-header-back">
          <button
            className="btn-icon"
            onClick={() => navigate(`/sites/${siteId}/content/categories`)}
            title="Back to categories"
          >
            ←
          </button>
          <h1>{isNew ? 'New Category' : 'Edit Category'}</h1>
        </div>
        <div className="page-header-actions">
          {isDirty && <span className="unsaved-indicator">Unsaved changes</span>}
          <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : isNew ? 'Create' : 'Save'}
          </button>
        </div>
      </div>

      <div className="editor-layout">
        <div className="editor-main">
          <div className="editor-form">
            {Object.entries(fields).map(([name, fieldSchema]) => (
              <FormField
                key={name}
                name={name}
                schema={fieldSchema}
                value={formData[name]}
                onChange={handleChange}
                siteId={siteId!}
              />
            ))}
          </div>
        </div>
        <HelpSidebar title={help.title} description={help.description} tips={help.tips} />
      </div>
    </div>
  );
}
